import { useRef, useEffect, useState, useContext, useCallback } from "react";
import {
  getGeneralCategoryLookupsById,
  postGetGenerateCategoriesWithQuestions,
  getTools,
} from "../../../../core/api/Api";
import { useParams, useLocation } from "react-router-dom";
import img from "../../../../assets/img/features/fi-1.svg";
import AnotherStoryGeneralCategoryOptions from "./AnotherStoryGeneralCategoryOptions";
import CategoryBasedTypes from "./CategoryBasedTypes";
import ToolChoice from "./ToolChoice";
import CategoryLengthTypes from "./CategoryLengthTypes";
import Loading from "../../../common/loading/Loading";
import { useTranslation } from "react-i18next";
import { showToast } from "../../../../core/common-service/CommonService";
import { StoreContext } from "../../../../core/context/StoreContext";
import useIsMobile from "../../../../core/hooks/useMobile";
import useLangNavigate from "../../../common/useLangNavigate";
import {
  SERVICE_SESSION_ID,
  TOOL_CHOICE_ID,
  TOOL_DISPLAY_ORDER,
  CATEGORY_ID_TO_LABEL,
} from "../../../../core/constants/constants";
import "./GeneralCategoriesOptions.css";
import coverPlaceholder from "../../../../assets/png/book-cover.png";
import LoadingCreateBook from "../../../common/loading/LoadingCreateBook";

const GeneralCategoriesOptions = () => {
  let { service_id, general_category_id, flow_type } = useParams();
  const location = useLocation();
  const writeMode =
    new URLSearchParams(location.search).get("write_mode") || "ai";
  const { isLoggedIn } = useContext(StoreContext);
  const navigate = useLangNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [is_autobio, setIsAutoBio] = useState(false);

  const [book_name, setBookName] = useState("");
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);

  const [steps, setSteps] = useState([1]);
  const isMobile = useIsMobile();
  const [tools, setTools] = useState([]);
  const [toolId, setToolId] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [bookCoverFile, setBookCoverFile] = useState(null);
  const uploadedCoverRef = useRef(null);

  const isNewBookFlow = flow_type === "new";
  const isReformulateFlow = flow_type === "reformulate";

  const postCreateAnotherStory = (
    category_based_type_ids,
    category_length_type_id,
    another_story_subject,
  ) => {
    if (
      !is_autobio &&
      !another_story_subject?.length &&
      toolId !== TOOL_CHOICE_ID.GRAMMAR_CORRECTION &&
      toolId !== TOOL_CHOICE_ID.REFORMULATE &&
      toolId !== TOOL_CHOICE_ID.HUMANIZE &&
      writeMode !== "self"
    )
      return;
    if (!toolId) return;

    setLoading(true);

    const formData = new FormData();
    formData.append("service_id", service_id);
    formData.append("general_category_id", general_category_id);
    formData.append(
      "category_based_type_ids",
      JSON.stringify(category_based_type_ids),
    );
    formData.append("category_length_type_id", category_length_type_id);
    formData.append("another_story_subject", another_story_subject);
    formData.append("book_name", book_name);
    formData.append("toolId", toolId);
    if (isNewBookFlow) {
      formData.append("write_mode", writeMode);
    }

    if (uploadedFile) {
      formData.append("word_file", uploadedFile);
    }

    postGetGenerateCategoriesWithQuestions(formData, isLoggedIn)
      .then((res) => {
        setLoading(false);
        if (res?.data?.success) {
          const data = res?.data?.success;

          const serviceSessionId = data?.id;
          const tempKey = "temp-cover-photo";
          const finalKey = serviceSessionId + "-image-cover-photo";
          const base64 = localStorage.getItem(tempKey);
          if (base64) {
            localStorage.setItem(finalKey, base64);
            localStorage.removeItem(tempKey);
          }

          if (!isLoggedIn) {
            data.checkpoint_data = JSON.parse(data.checkpoint_data);
            localStorage.setItem("trial_session", JSON.stringify(data));
          }

          if (data?.is_generating) {
            // localStorage.setItem(`book-generating-${serviceSessionId}`, "true");
            navigate("/my-books");
            showToast({
              type: "success",
              message: t("please_come_later_to_get_your_book"),
            });
          } else {
            navigate("/questions/" + data?.id);
          }

          window.scrollTo(0, 0);
        }
      })
      .catch((err) => {
        setLoading(false);
        showToast({
          type: "error",
          message: t("error.error_in_your_request"),
        });
      });
  };

  const goToQuestions = () => {
    const another_story_subject =
      data.general_categories[0]?.question_categories[0]?.questions[0]?.transcript?.trim() ??
      "";
    const category_length_type_id = data["category_length_types"].find(
      (el) => el?.is_selected,
    )?.id;
    const selectedCategoryBasedTypeIds = data["category_based_types"]
      .filter((el) => el?.is_selected)
      .map((el) => el.id);

    if (
      !is_autobio &&
      toolId !== TOOL_CHOICE_ID.GRAMMAR_CORRECTION &&
      toolId !== TOOL_CHOICE_ID.REFORMULATE &&
      toolId !== TOOL_CHOICE_ID.HUMANIZE
    ) {
      if (writeMode === "self") {
        // Description is optional for self-write: only validate if they started writing
        if (another_story_subject.length > 0 && another_story_subject.length < 100) {
          showToast({
            type: "error",
            message: t("error.description_should_be_more_than_100_char"),
          });
          return;
        }
      } else {
        // AI mode: description required and must be ≥ 100 chars
        if (another_story_subject.length < 100) {
          showToast({
            type: "error",
            message: t("error.description_should_be_more_than_100_char"),
          });
          return;
        }
      }
    }

    if (!is_autobio && another_story_subject.length >= 2000) {
      showToast({
        type: "error",
        message: t("error.description_should_be_less_than_2000_char"),
      });
      return;
    }

    if (selectedCategoryBasedTypeIds && category_length_type_id) {
      postCreateAnotherStory(
        selectedCategoryBasedTypeIds,
        category_length_type_id,
        another_story_subject,
      );
    } else {
      showToast({
        type: "error",
        message: t("error.please_select_required_boxes"),
      });
    }
  };

  const onLookupClick = (type, item) => () => {
    const tmpData = { ...data };

    tmpData[type] = tmpData[type].map((el) => {
      if (el.group_id === item.group_id) {
        return {
          ...el,
          is_selected: el.id === item.id,
        };
      }
      return {
        ...el,
        is_selected: el.is_selected,
      };
    });

    if (!steps.includes(2)) {
      setSteps([...steps, 2]);
    }

    const targetId =
      type === "category_based_types"
        ? "another_story_category"
        : "another_story_proceed_btn";

    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "start",
      });
    }

    setData(tmpData);
  };

  const onBookNameChange = (e) => {
    e.preventDefault();
    setBookName(e.target.value);
  };

  const getData = useCallback(() => {
    setLoading(true);
    getTools().then((res) => {
      setTools(res.data);
    });

    getGeneralCategoryLookupsById(general_category_id)
      .then((res) => {
        setLoading(false);
        if (mounted.current && res?.data?.success) {
          const data = res.data.success;
          setIsAutoBio(
            data?.general_categories?.[0].id === SERVICE_SESSION_ID.IS_AUTOBIO,
          );
          setData(data);
        }
      })
      .catch((e) => {
        if (mounted.current) setLoading(false);
      });
  }, [general_category_id]);

  const onResult =(index, q_index) => (transcript_str) => {
    if (!steps.includes(2)) {
      setSteps([...steps, 2]);
    }
    let tmp_data = { ...data };
    tmp_data.general_categories[0].question_categories[index].questions[
      q_index
    ].transcript = transcript_str;
    setData(tmp_data);
  };

  const onSubmitAnotherStory = (item, index) => () => {
    onLookupClick("category_based_types", data.category_based_types[0])();
    goToQuestions();
  };

  const onInit = useRef(true);

  const blobToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleCoverUpload = async (file) => {
    const base64 = await blobToBase64(file);
    uploadedCoverRef.current = base64;
    setBookCoverFile(file);
    localStorage.setItem("temp-cover-photo", base64);
  };

  useEffect(() => {
    if (isNewBookFlow) {
      setToolId(TOOL_CHOICE_ID.NEW);
    }
  }, [isNewBookFlow]);

  useEffect(() => {
    if (!onInit.current) return;
    onInit.current = false;
    mounted.current = true;
    getData();
    return () => {
      mounted.current = false;
    };
  }, [getData]);

  useEffect(() => {}, [steps]);

  useEffect(() => {
    if (loading) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [loading]);

  if (data?.length === 0) {
    return <Loading />;
  }

  if (loading) {
    return <LoadingCreateBook />;
  }

  return (
    <section className="aai-features py-100">
      <div className="container">
        <div className="row justify-content-left">
          <div className="col-xl-8">
            <div className="aai-section-title mb-5">
              <h2 className="section-title">
                {t(
                  isReformulateFlow
                    ? "what_project_to_bring_to_life"
                    : "what_do_you_want_to_write_about",
                )}
                ?
              </h2>
              <p className="section-desc">
                {t(
                  isReformulateFlow
                    ? "here_you_can_write_about_anything_you_want_reformulate"
                    : "here_you_can_write_about_anything_you_want",
                )}
              </p>
            </div>
          </div>
        </div>
        <div className="book-section-split">
          <div className="left-section text-center">
            <div
              style={{
                width: "220px",
                margin: "0 auto",
                position: "relative",
                backgroundColor: "white",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: "20px",
                  background: "linear-gradient(to right, #f0f0f0, #fafafa)",
                  borderRight: "1px solid #e0e0e0",
                }}
              />

              <div
                style={{ paddingLeft: "20px", padding: "30px 20px 30px 35px" }}
              >
                <div
                  className="cursor-pointer"
                  onClick={() =>
                    document.getElementById("cover-upload-input").click()
                  }
                  style={{
                    width: "140px",
                    height: "140px",
                    border: bookCoverFile ? "none" : "2px dashed #FF6A00",
                    borderRadius: "4px",
                    margin: "0 auto",
                    backgroundColor: bookCoverFile ? "transparent" : "#FDF5F1",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "relative",
                  }}
                >
                  <img
                    src={
                      bookCoverFile
                        ? uploadedCoverRef.current
                        : coverPlaceholder
                    }
                    alt="cover preview"
                    style={{
                      width: bookCoverFile ? "100%" : "60%",
                      height: bookCoverFile ? "100%" : "auto",
                      objectFit: "cover",
                      borderRadius: "4px",
                    }}
                  />

                  {bookCoverFile && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setBookCoverFile(null);
                        uploadedCoverRef.current = null;
                        document.getElementById("cover-upload-input").value =
                          "";
                      }}
                      style={{
                        position: "absolute",
                        top: "-5px",
                        right: "-5px",
                        width: "24px",
                        height: "24px",
                        borderRadius: "50%",
                        backgroundColor: "#FF6A00",
                        color: "white",
                        border: "2px solid white",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "14px",
                        fontWeight: "bold",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                        zIndex: 10,
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>

                <div
                  className="mt-3"
                  style={{
                    color: "#c1471f",
                    fontWeight: 500,
                    fontSize: "13px",
                  }}
                >
                  {t("upload_cover_photo")}
                </div>

                <div
                  className="mt-4"
                  style={{ color: "#333", fontSize: "15px", fontWeight: 500 }}
                >
                  {t(
                    `book_type.${
                      CATEGORY_ID_TO_LABEL[data?.general_categories?.[0].id]
                    }`,
                  )}
                </div>
              </div>
            </div>

            <input
              id="cover-upload-input"
              type="file"
              accept="image/jpeg, image/png"
              className="d-none"
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;

                if (file.size > 300 * 1024) {
                  showToast({
                    type: "error",
                    message: t("image_is_too_big"),
                  });
                  return;
                }

                await handleCoverUpload(file);
              }}
            />
          </div>
          <div className="right-section">
            <div className="row mb-5">
              <div className="aai-form-container">
                <h4 className="section-desc mb-3">{t("book_name")}</h4>
                <div
                  className={`d-flex ${isMobile ? "flex-column" : "flex-row"}`}
                >
                  <input
                    className="form-control shadow-none"
                    id="book_name"
                    name="book_name"
                    type="text"
                    placeholder={t("enter_your_book_name")}
                    value={book_name}
                    onChange={onBookNameChange}
                    style={{
                      width: isMobile ? "100%" : "80%",
                      marginRight: isMobile ? "0rem" : "1rem",
                      marginBottom: isMobile ? "1rem" : "0rem",
                      backgroundColor: "white",
                    }}
                  />
                </div>
              </div>
            </div>

            {isReformulateFlow && (
              <div id="tool_choice">
                <ToolChoice
                  tools={tools
                    .filter(
                      (tool) =>
                        ![
                          TOOL_CHOICE_ID.NEW,
                          TOOL_CHOICE_ID.CREATE_FROM_NOTES,
                          TOOL_CHOICE_ID.PLAN_FROM_NOTES,
                        ].includes(tool.id),
                    )
                    .sort((a, b) => {
                      return (
                        (TOOL_DISPLAY_ORDER[a.id] || 99) -
                        (TOOL_DISPLAY_ORDER[b.id] || 99)
                      );
                    })}
                  selectedOption={toolId}
                  onSelect={(id) => {
                    setToolId(id);

                    if (
                      (id === TOOL_CHOICE_ID.REFORMULATE ||
                        id === TOOL_CHOICE_ID.GRAMMAR_CORRECTION ||
                        id === TOOL_CHOICE_ID.HUMANIZE) &&
                      data?.category_length_types
                    ) {
                      const oneLengthCategoryType =
                        data.category_length_types.find(
                          (item) => item.new_book === 0,
                        );
                      if (oneLengthCategoryType) {
                        onLookupClick(
                          "category_length_types",
                          oneLengthCategoryType,
                        )();
                      }
                    }

                    const targetElement = document.getElementById(
                      "category_based_types",
                    );
                    if (targetElement) {
                      targetElement.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                        inline: "start",
                      });
                    }
                  }}
                  isVisible={steps.includes(1)}
                />
              </div>
            )}

            {!isReformulateFlow && (
                <div id="category_based_types">
                  <CategoryBasedTypes
                    data={data}
                    img={img}
                    onLookupClick={onLookupClick}
                    isVisible={steps.includes(1)}
                    refomulateAutobio={
                      toolId === TOOL_CHOICE_ID.REFORMULATE && is_autobio
                    }
                  />
                </div>
              )}

            {isReformulateFlow && steps.includes(1) && (
              <div className="custom-upload-container">
                <label
                  htmlFor="file-upload"
                  className={`upload-box ${uploadedFile ? "has-file" : ""}`}
                >
                  <div className="upload-icon">{uploadedFile ? "✓" : "↑"}</div>
                  <div className="upload-title">
                    {uploadedFile
                      ? t("file_uploaded")
                      : t("upload_your_document")}
                  </div>
                  <div className="upload-subtext">
                    {uploadedFile
                      ? uploadedFile.name
                      : t("upload_draft_word_file")}
                  </div>
                </label>
                <input
                  id="file-upload"
                  type="file"
                  accept=".doc,.docx"
                  onChange={(e) => setUploadedFile(e.target.files[0])}
                  className="file-input-hidden"
                />
              </div>
            )}

            {!isReformulateFlow && (
                <div id="another_story_category">
                  {writeMode === "self" && steps.includes(2) && (
                    <p className="text-muted text-center" style={{ fontSize: "14px", marginBottom: "4px" }}>
                      {t("self_write_description_optional_hint")}
                    </p>
                  )}
                  <AnotherStoryGeneralCategoryOptions
                    data={data}
                    onResult={onResult}
                    isVisible={steps.includes(2)}
                    questionLabelOverride={
                      isReformulateFlow
                        ? t("describe_manuscript_to_improve")
                        : undefined
                    }
                  />
                </div>
              )}

            <CategoryLengthTypes
              data={data}
              img={img}
              onLookupClick={onLookupClick}
              isVisible={
                steps.includes(2) &&
                (isNewBookFlow || (isReformulateFlow && toolId))
              }
              one_length_book={
                isReformulateFlow &&
                (toolId === TOOL_CHOICE_ID.REFORMULATE ||
                  toolId === TOOL_CHOICE_ID.GRAMMAR_CORRECTION ||
                  toolId === TOOL_CHOICE_ID.HUMANIZE)
              }
              toolId={toolId}
            />

            {steps.includes(2) ||
            (steps.includes(1) &&
              (toolId === TOOL_CHOICE_ID.GRAMMAR_CORRECTION ||
                toolId === TOOL_CHOICE_ID.REFORMULATE ||
                toolId === TOOL_CHOICE_ID.HUMANIZE)) ? (
              <div
                id="another_story_proceed_btn"
                className="d-flex"
                style={{ marginTop: "2rem" }}
              >
                <button
                  className="start-writing"
                  type="submit"
                  disabled={loading}
                  onClick={onSubmitAnotherStory(
                    data?.general_categories?.[0]?.question_categories[0],
                    0,
                  )}
                >
                  {t(
                    toolId === TOOL_CHOICE_ID.GRAMMAR_CORRECTION
                      ? "start_correction"
                      : toolId === TOOL_CHOICE_ID.REFORMULATE
                      ? "start_reformulation"
                      : toolId === TOOL_CHOICE_ID.HUMANIZE
                      ? "start_humanization"
                      : "start_writing",
                  )}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};

export default GeneralCategoriesOptions;
